import type { UseNavigateResult } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import type { BillingPeriodType } from '@/shared/lib/billing-period-type/resolveBillingPeriodType';
import { i18n } from '@/shared/lib/i18n';
import { toast } from '@/shared/ui';

/**
 * 非同期ジョブ開始時のトースト表示
 *
 * 1行目: 「{jobName}を開始しました」
 * 2行目: 「結果はジョブ管理で確認してください」
 * cancel: 「閉じる」ボタン
 * action: 「ジョブ管理」ボタン → /job へ遷移
 *
 * @param t - i18n翻訳関数
 * @param navigate - TanStack Router の navigate 関数
 * @param jobName - ジョブ名（例: 「スキルの一括実行」「事業の一括作成」）
 */
export const showAsyncJobStartedToast = ({
  t,
  navigate,
  jobName,
}: {
  t: TFunction;
  navigate: UseNavigateResult<string>;
  jobName: string;
}) => {
  toast.success(t('common.messages.async-job-started-title', { jobName }), {
    description: t('common.messages.async-job-started-description'),
    cancel: {
      label: t('common.actions.close'),
      onClick: () => {},
    },
    action: {
      label: t('job.navigation.title'),
      onClick: () => {
        navigate({ to: '/job' });
      },
    },
  });
};

/**
 * AIクレジット上限到達時のトースト表示
 *
 * 1行目: 「AIクレジットが上限に達しました」
 * 2行目: 契約サイクル（monthly / yearly）に応じて description を切り替える
 * cancel: 「閉じる」ボタン
 * action: 「プランと使用量を確認」ボタン → /plan-usage へ遷移
 */
export const showTokenLimitExceededToast = ({
  toastId,
  navigate,
  billingPeriodType,
}: {
  toastId: string;
  navigate: UseNavigateResult<string>;
  billingPeriodType: BillingPeriodType;
}) => {
  const descriptionMap = {
    monthly: i18n.t('ai.errors.token-limit-exceeded-description-monthly'),
    yearly: i18n.t('ai.errors.token-limit-exceeded-description-yearly'),
  };
  toast.error(i18n.t('plan-usage.alert.limit-reached-title'), {
    id: toastId,
    description: descriptionMap[billingPeriodType],
    style: { width: '420px' },
    cancel: {
      label: i18n.t('common.actions.close'),
      onClick: () => {},
    },
    action: {
      label: i18n.t('ai.errors.token-limit-exceeded-button'),
      onClick: () => {
        navigate({ to: '/plan-usage' });
      },
    },
  });
};
